import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { GripVertical, X, AlertCircle } from 'lucide-react';
import { DraftPlayer } from '@/types/draftPlayer';
import { AppliedDraftSabotage } from '@/types/draftSabotage';
import { cn } from '@/lib/utils';
import { formatPositionDisplay } from '@/lib/positionEligibility';
import { ActiveNewsProvider } from '@/components/player/ActiveNewsContext';
import PlayerNewsIcon from '@/components/player/PlayerNewsIcon';

interface PlayerQueueProps {
  queuedPlayers: DraftPlayer[];
  onRemoveFromQueue: (playerId: string) => void;
  onDraftPlayer: (playerId: string) => void;
  isOnTheClock: boolean;
  isShuffled: boolean;
  revealedByScoutReport: boolean;
}

const positionColors: Record<string, string> = {
  QB: 'bg-red-500/20 text-red-400',
  RB: 'bg-green-500/20 text-green-400',
  WR: 'bg-blue-500/20 text-blue-400',
  TE: 'bg-orange-500/20 text-orange-400',
  K: 'bg-purple-500/20 text-purple-400',
  DEF: 'bg-yellow-500/20 text-yellow-400',
  C: 'bg-amber-500/20 text-amber-400',
  '1B': 'bg-green-500/20 text-green-400',
  '2B': 'bg-blue-500/20 text-blue-400',
  '3B': 'bg-red-500/20 text-red-400',
  SS: 'bg-purple-500/20 text-purple-400',
  OF: 'bg-cyan-500/20 text-cyan-400',
  SP: 'bg-orange-500/20 text-orange-400',
  RP: 'bg-pink-500/20 text-pink-400',
  DH: 'bg-yellow-500/20 text-yellow-400',
  UTIL: 'bg-teal-500/20 text-teal-400',
};

const PlayerQueue = ({ 
  queuedPlayers, 
  onRemoveFromQueue, 
  onDraftPlayer,
  isOnTheClock,
  isShuffled,
  revealedByScoutReport
}: PlayerQueueProps) => {
  return (
    <ActiveNewsProvider sport="mlb" keys={queuedPlayers.map(p => p.name)}>
    <Card className={cn(
      "glass-card",
      isShuffled && "border-warning/50 animate-pulse",
      revealedByScoutReport && "border-damage/50"
    )}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          ⭐ My Queue
          <Badge variant="outline" className="ml-auto">{queuedPlayers.length}</Badge>
        </CardTitle>
        {isShuffled && (
          <div className="flex items-center gap-2 text-warning text-sm bg-warning/10 rounded-lg p-2">
            <AlertCircle className="w-4 h-4" />
            Your queue was shuffled by a sabotage!
          </div>
        )}
        {revealedByScoutReport && (
          <div className="flex items-center gap-2 text-damage text-sm bg-damage/10 rounded-lg p-2">
            <AlertCircle className="w-4 h-4" />
            Your top 3 picks are visible to others!
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="h-[150px]">
          <div className="space-y-1 p-4 pt-0">
            {queuedPlayers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>Your queue is empty</p>
                <p className="text-sm">Add players from the list to pre-rank them</p>
              </div>
            ) : (
              queuedPlayers.map((player, index) => (
                <div
                  key={player.id}
                  className={cn(
                    "flex items-center gap-2 p-2 rounded-lg bg-muted/30 group hover:bg-muted/50 transition-all",
                    index === 0 && isOnTheClock && "ring-2 ring-primary ring-offset-2 ring-offset-background"
                  )}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab" />
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-mono">
                    {index + 1}
                  </div>
                  <Badge className={cn("text-xs shrink-0", positionColors[player.position])}>
                    {formatPositionDisplay(player.position, player.position_eligibility)}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-sm truncate">{player.name}</span>
                      <PlayerNewsIcon playerName={player.name} />
                    </div>
                    <span className="text-xs text-muted-foreground">{player.team}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    {isOnTheClock && index === 0 && (
                      <Button
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => onDraftPlayer(player.id)}
                      >
                        Draft
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-damage"
                      onClick={() => onRemoveFromQueue(player.id)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
    </ActiveNewsProvider>
  );
};

export default PlayerQueue;
